import { DownloadIcon } from "@xola/icons";
import { Badge, Button } from "@xola/ui-kit";
import { useMount } from "ahooks";
import { DateFormat, formatDate } from "common";
import DOMPurify from "dompurify";
import React from "react";
import { Email } from "types";
import { sanitizeFilename } from "./sanitizedEmailFilename";
import { NoResults } from "@/components/Common/NoResults";
import { Loader } from "@/components/Loader";
import { useEmail } from "@/hooks/resources/useEmail";

interface EmailDetailsProps {
    email?: Email;
    showDownloadButton?: boolean;
}

export const EmailDetails = ({ email, showDownloadButton = true }: EmailDetailsProps) => {
    const { data: fullEmail, refetch } = useEmail(email?.id);

    useMount(() => {
        if (email && !email.body) {
            refetch();
        }
    });

    if (!email) {
        return <NoResults>No Email Selected</NoResults>;
    }

    const displayEmail = fullEmail ?? email;

    const safeHtml = displayEmail.body
        ? DOMPurify.sanitize(displayEmail.body, { ADD_ATTR: ["target"], WHOLE_DOCUMENT: true })
        : undefined;

    const handleDownloadEmail = () => {
        if (!safeHtml) return;

        const blob = new Blob([safeHtml], { type: "text/html" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        const fileName = `${sanitizeFilename(displayEmail.subject)}.html`;
        link.href = url;
        link.download = fileName;
        document.body.append(link);
        link.click();

        link.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="ui-email-details flex h-full flex-col divide-y divide-gray-light">
            <header className="shrink-0 px-10 py-6">
                <div className="flex items-center justify-between">
                    <h3>{displayEmail.subject}</h3>
                    {showDownloadButton && (
                        <Button icon={<DownloadIcon />} onClick={handleDownloadEmail}>
                            Download Email
                        </Button>
                    )}
                </div>

                <div className="flex w-full pt-4">
                    <div className="grow space-y-1">
                        <div>
                            <Badge size="small">From</Badge> {displayEmail.from.name ?? "??"} &lt;
                            {displayEmail.from.address}&gt;
                        </div>
                        <div className="pl-4">
                            <Badge size="small">To</Badge>{" "}
                            {displayEmail.to.map((recipient, index) => (
                                <span key={recipient.address ?? index} className="inline-block whitespace-nowrap">
                                    <strong>{recipient.name ?? "??"}</strong> &lt;{recipient.address}&gt;
                                    {index < displayEmail.to.length - 1 && ", "}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="w-40 shrink-0 text-right">
                        <p>{formatDate(displayEmail.createdAt, DateFormat.DATE_TIME_SHORT)}</p>
                        <sup>{displayEmail.createdAt.fromNow()}</sup>
                    </div>
                </div>
            </header>
            {safeHtml ? (
                <iframe
                    title="Email body"
                    srcDoc={`<!doctype html>${safeHtml.replace(/<a /g, '<a target="_blank"')}`}
                    sandbox="allow-popups allow-popups-to-escape-sandbox"
                    className="min-h-0 w-full grow"
                    data-testid="email-details-body-frame"
                />
            ) : (
                <Loader size="tiny" className="p-2" text="Fetching Content" />
            )}
        </div>
    );
};
